import React from 'react';
import { i18n } from 'config/locales';
import { updateApplication } from '@redux/modules/application/actions';
import { updateSubsectionRequest } from '@redux/modules/form/actions';
import { saveRequest } from '@redux/modules/save/actions';
import { CITIZENSHIP, CITIZENSHIP_STATUS, CITIZENSHIP_US_PASSPORT } from 'config/formSections/citizenship';
import { FOREIGN } from 'config/formSections/foreign';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Name from 'components/Form/Name';
import PVQName from 'components/Form/PVQName';
import Branch from 'components/Form/Branch';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import Dropdown from 'components/Form/Dropdown';
import * as formTypes from 'constants/formTypes';
import { CITIZENSHIP_PASSPORTS } from 'constants/sections';
import Comments from 'components/Form/Comments';
import {
  FOREIGN_BORN,
  NATURALIZED,
  DERIVED,
  NOT_CITIZEN,
  notCitizenDocumentTypes,
  CITIZEN,
  citizenshipStatusOptions,
  pvqCitizenshipStatusOptions,
  PVQ_CITIZEN,
  PVQ_FOREIGN_BORN,
  PVQ_NATURALIZED,
  PVQ_DERIVED,
  PVQ_NOT_CITIZEN,
  PVQ_NATIONAL_BY_BIRTH_IN_US,
  PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US,
  isNationalOrNonCitizen
} from 'constants/enums/citizenshipOptions';
import {
  dateIsEmpty,
  nameIsEmpty,
  locationCityStateIsEmpty,
  locationCityCountryIsEmpty,
  valueIsEmpty,
} from 'models/validate';
import NotApplicable from 'components/Form/NotApplicable';
import { residencyStatusOptions } from 'constants/enums/residencyStatuses';
import { isForeignBornUSCitizen } from '../../../../validators/passport';
import { countryString } from '../../../../validators/location';
import { isDocumentRequired, isCertificateRequired } from '../../../../validators/citizenship';
import { alphaNumericRegEx, validGenericTextfield } from '../../../../validators/helpers';

const sectionConfig = {
  key: CITIZENSHIP_STATUS.key,
  section: CITIZENSHIP.name,
  store: CITIZENSHIP.store,
  subsection: CITIZENSHIP_STATUS.name,
  storeKey: CITIZENSHIP_STATUS.storeKey,
  displayErrorsReviewOnly: ['citizenship.errors.missingRequiredDocumentsSF852013']
};

export class Status extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.handleNameError = this.handleNameError.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      formType, formVersion, dispatch, Citizenship,
    } = this.props;

    const shouldSaveForeignPassport = Citizenship?.Status?.CitizenshipStatus?.value !== prevProps.Citizenship?.Status?.CitizenshipStatus?.value
      && !isForeignBornUSCitizen({ Citizenship });
    if (isFormSF852013(formType, formVersion) && shouldSaveForeignPassport) {
      // Need to save data after change, since it lies outside the normal section data (section data saves on navigating away from section)
      // See updateForeignPassport for additional context
      dispatch(saveRequest(FOREIGN.key, CITIZENSHIP_US_PASSPORT.key));
    }
  }

  handleNameError(value, arr) {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.indexOf('required') !== -1
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      CitizenshipStatus: this.props.CitizenshipStatus,
      CitizenshipStatusComments: this.props.CitizenshipStatusComments,
      AlienRegistrationComments: this.props.AlienRegistrationComments,
      AlienRegistrationNumberComments: this.props.AlienRegistrationNumberComments,
      DerivedComments: this.props.DerivedComments,
      MilitaryBaseComments: this.props.MilitaryBaseComments,
      ProofOfUSCitizenshipComments: this.props.ProofOfUSCitizenshipComments,
      CertificateComments: this.props.CertificateComments,
      NaturalizationComments: this.props.NaturalizationComments,
      AbroadDocumentation: this.props.AbroadDocumentation,
      Explanation: this.props.Explanation,
      DocumentTitle: this.props.DocumentTitle,
      EntryDateExplanation: this.props.EntryDateExplanation,
      EntryLocationExplanation: this.props.EntryLocationExplanation,
      AlienRegistrationNumberExplanation: this.props.AlienRegistrationNumberExplanation,
      DocumentNumber: this.props.DocumentNumber,
      DocumentNumberNotApplicable: this.props.DocumentNumberNotApplicable,
      DocumentIssued: this.props.DocumentIssued,
      PlaceIssued: this.props.PlaceIssued,
      DocumentName: this.props.DocumentName,
      CertificateNumber: this.props.CertificateNumber,
      CertificateIssued: this.props.CertificateIssued,
      CertificateName: this.props.CertificateName,
      CertificateMotherMaidenName: this.props.CertificateMotherMaidenName,
      BornOnMilitaryInstallation: this.props.BornOnMilitaryInstallation,
      MilitaryBase: this.props.MilitaryBase,
      EntryDate: this.props.EntryDate,
      EntryDateNotApplicable: this.props.EntryDateNotApplicable,
      EntryLocation: this.props.EntryLocation,
      EntryLocationNotApplicable: this.props.EntryLocationNotApplicable,
      PriorCitizenship: this.props.PriorCitizenship,
      HasAlienRegistration: this.props.HasAlienRegistration,
      AlienRegistrationNumber: this.props.AlienRegistrationNumber,
      AlienRegistrationNumberNotApplicable: this.props.AlienRegistrationNumberNotApplicable,
      AlienRegistrationExpiration: this.props.AlienRegistrationExpiration,
      CertificateCourtName: this.props.CertificateCourtName,
      CertificateCourtAddress: this.props.CertificateCourtAddress,
      Basis: this.props.Basis,
      PermanentResidentCardNumber: this.props.PermanentResidentCardNumber,
      CitizenshipCertificateCardNumberNotApplicable: this.props.CitizenshipCertificateCardNumberNotApplicable,
      ResidenceStatus: this.props.ResidenceStatus,
      ResidenceStatusExplanation: this.props.ResidenceStatusExplanation,
      DocumentType: this.props.DocumentType,
      DocumentExpiration: this.props.DocumentExpiration,
      DocumentExpirationNotApplicable: this.props.DocumentExpirationNotApplicable,
      MaidenName: this.props.MaidenName,
      Form240Date: this.props.Form240Date,
      Form240DateExplanation: this.props.Form240DateExplanation,
      IsResidentOfUnitedStates: this.props.IsResidentOfUnitedStates,
      ...queue,
    }, [CITIZENSHIP_PASSPORTS]);
  };

  updateField = (field, values) => {
    this.update({
      [field]: values,
    });
  };

  updateEntryDateNotApplicable = (value) => {
    this.update({
      EntryDateNotApplicable: value,
      EntryDate: value.applicable ? this.props.EntryDate : {},
      EntryDateExplanation: value.applicable ? this.props.EntryDateExplanation : {},
      EntryLocation: value.applicable ? this.props.EntryLocation : {},
    });
  };

  updateEntryLocationNotApplicable = (value) => {
    this.update({
      EntryLocationNotApplicable: value,
      EntryLocation: value.applicable ? this.props.EntryLocation : {},
      EntryLocationExplanation: value.applicable ? this.props.EntryLocationExplanation : {},
    });
  };

  updateAlienRegistrationNumberNotApplicable = (value) => {
    this.update({
      AlienRegistrationNumberNotApplicable: value,
      AlienRegistrationNumber: value.applicable ? this.props.AlienRegistrationNumber : {},
      AlienRegistrationNumberExplanation: value.applicable ? this.props.AlienRegistrationNumberExplanation : {},
    });
  };

  updateDocumentNumberNotApplicable = (value) => {
    this.update({
      DocumentNumberNotApplicable: value,
      DocumentNumber: value.applicable ? this.props.DocumentNumber : {},
    });
  };

  updateCitizenshipCertificateCardNumberNotApplicable = (value) => {
    this.update({
      CitizenshipCertificateCardNumberNotApplicable: value,
      CertificateNumber: value.applicable ? this.props.CertificateNumber : {}, // change to certifcate number

    });
  };

  updateAbroadDocumentation = (values) => {
    const updatedDocumentTitle = values?.value !== 'Other' ? { DocumentTitle: {} } : {};
    this.update({
      AbroadDocumentation: values,
      ...updatedDocumentTitle
    });
  };

  updateDocumentExpirationNotApplicable = (value) => {
    this.update({
      DocumentExpirationNotApplicable: value,
      DocumentExpiration: value.applicable ? this.props.DocumentExpiration : {},
    });
  };

  updateIsResidentOfUnitedStates = (values) => {
    if (values.value === 'No') {
      this.update({
        IsResidentOfUnitedStates: values,
        ResidenceStatus: {},
        AlienRegistrationNumber: {},
        AlienRegistrationNumberNotApplicable: { applicable: true },
        AlienRegistrationNumberExplanation: {},
        DocumentExpiration: {},
        DocumentExpirationNotApplicable: { applicable: true },
        DocumentType: {},
        DocumentTitle: {},
        EntryDateNotApplicable: { applicable: true },
        EntryLocationNotApplicable: { applicable: true },
        CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
        Explanation: {},
        DocumentNumber: {},
        DocumentNumberNotApplicable: { applicable: true },
        DocumentIssued: {},
        AlienRegistrationExpiration: {},
        DocumentName: {},
      });
    } else {
      this.update({
        IsResidentOfUnitedStates: values,
      });
    }
  };

  getForeignBornStatusUpdate = (value) => (value === FOREIGN_BORN || value === PVQ_FOREIGN_BORN ? {} : {
    AbroadDocumentation: {},
    Explanation: {},
    DocumentTitle: {},
    DocumentNumber: {},
    DocumentIssued: {},
    PlaceIssued: {},
    DocumentName: {},
    CertificateNumber: {},
    CertificateIssued: {},
    CertificateName: {},
    BornOnMilitaryInstallation: {},
    ProofOfUSCitizenshipComments: {},
    MilitaryBase: {},
    MilitaryBaseComments: {},
    DocumentNumberNotApplicable: { applicable: true },
    EntryDateNotApplicable: { applicable: true },
    EntryLocationNotApplicable: { applicable: true },
    AlienRegistrationNumberNotApplicable: { applicable: true },
    CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
    DocumentExpirationNotApplicable: { applicable: true },
  });

  getNaturalizedStatusUpdate = (value) => (value === NATURALIZED || value === PVQ_NATURALIZED ? {} : {
    EntryDate: {},
    EntryDateNotApplicable: { applicable: true },
    EntryLocation: {},
    EntryLocationNotApplicable: { applicable: true },
    PriorCitizenship: {},
    HasAlienRegistration: {},
    NaturalizationComments: {},
    AlienRegistrationNumber: {},
    AlienRegistrationNumberComments: {},
    CertificateNumber: {},
    CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
    CertificateCourtName: {},
    CertificateCourtAddress: {},
    CertificateIssued: {},
    CertificateName: {},
    DocumentNumber: {},
    DocumentIssued: {},
    PlaceIssued: {},
    Form240Date: {},
    Form240DateExplanation: {},
    Basis: {},
    Explanation: {},
    DocumentTitle: {},
    CertificateComments: {},
  });

  getDerivedStatusUpdate = (value) => (value === DERIVED || value === PVQ_DERIVED ? {} : {
    AlienRegistrationNumber: {},
    AlienRegistrationNumberNotApplicable: { applicable: true },
    AlienRegistrationNumberExplanation: {},
    PermanentResidentCardNumber: {},
    CertificateNumber: {},
    CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
    CertificateName: {},
    CertificateIssued: {},
    Basis: {},
    Explanation: {},
    DocumentTitle: {},
    DerivedComments: {},
    DocumentNumberNotApplicable: { applicable: true },
    EntryDateNotApplicable: { applicable: true },
    EntryLocationNotApplicable: { applicable: true },
    DocumentExpirationNotApplicable: { applicable: true },
  });

  getNotCitizenStatusUpdate = (value) => (value === NOT_CITIZEN || value === PVQ_NOT_CITIZEN ? {} : {
    ResidenceStatus: {},
    EntryDate: {},
    EntryDateNotApplicable: { applicable: true },
    EntryDateExplanation: {},
    PriorCitizenship: {},
    EntryLocation: {},
    EntryLocationNotApplicable: { applicable: true },
    EntryLocationExplanation: {},
    AlienRegistrationNumber: {},
    AlienRegistrationNumberNotApplicable: { applicable: true },
    AlienRegistrationNumberExplanation: {},
    DocumentExpiration: {},
    DocumentType: {},
    Explanation: {},
    DocumentTitle: {},
    DocumentNumber: {},
    DocumentName: {},
    DocumentIssued: {},
    AlienRegistrationExpiration: {},
    AlienRegistrationComments: {},
    DocumentNumberNotApplicable: { applicable: true },
    CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
    DocumentExpirationNotApplicable: { applicable: true },
  });

  updateCitizenshipStatus = (values) => {
    const { formType, formVersion } = this.props;
    this.update({
      CitizenshipStatus: values,
      ...this.getForeignBornStatusUpdate(values?.value),
      ...this.getNaturalizedStatusUpdate(values?.value),
      ...this.getDerivedStatusUpdate(values?.value),
      ...this.getNotCitizenStatusUpdate(values?.value),
    });

    // US Passport gets hidden in SF85 2013, so need to reset values
    if (isFormSF852013(formType, formVersion) && values.value !== 'Naturalized') {
      this.updateForeignPassport(values);
    }
  };

  // US Passport data falls under Foreign.Passports; this is the reason for the alternate update (outside of Citizenship Status section)
  updateForeignPassport = (values) => {
    const { dispatch } = this.props;
    dispatch(updateApplication(FOREIGN.store, CITIZENSHIP_US_PASSPORT.storeKey, {}));
    dispatch(updateSubsectionRequest('CITIZENSHIP_US_PASSPORT', values, [CITIZENSHIP_US_PASSPORT.storeKey]));
  };

  updatePlaceIssued = (values) => {
    const { PlaceIssued } = this.props;
    // If country changes, it means the branch changed.
    // This means we need to clear out the state value
    if (countryString(PlaceIssued.country) !== countryString(values?.country)) {
      this.update({
        PlaceIssued: {
          ...values,
          state: undefined,
        },
      });
    } else {
      this.update({
        PlaceIssued: values,
      });
    }
  };

  updateCitizenshipStatusComments = (values) => {
    this.update({
      CitizenshipStatusComments: values,
    });
  };

  updateAlienRegistrationComments = (values) => {
    this.update({
      AlienRegistrationComments: values,
    });
  };

  updateAlienRegistrationNumberComments = (values) => {
    this.update({
      AlienRegistrationNumberComments: values,
    });
  };

  updateDerivedComments = (values) => {
    this.update({
      DerivedComments: values,
    });
  };

  updateMilitaryBaseComments = (values) => {
    this.update({
      MilitaryBaseComments: values,
    });
  };

  updateProofOfUSCitizenshipComments = (values) => {
    this.update({
      ProofOfUSCitizenshipComments: values,
    });
  };

  updateCertificateComments = (values) => {
    this.update({
      CertificateComments: values,
    });
  };

  updateNaturalizationComments = (values) => {
    this.update({
      NaturalizationComments: values,
    });
  };

  updateBasis = (values) => {
    const isOther = values.value === 'Other';
    this.update({
      Basis: values,
      Explanation: isOther ? this.props.Explanation : {}
    });
  };

  updateDocumentType = (values) => {
    const isOther = values.value === 'Other';
    this.update({
      DocumentType: values,
      Explanation: isOther ? this.props.Explanation : {},
      DocumentNumber: isOther ? {} : this.props.DocumentNumber,
      DocumentName: isOther ? {} : this.props.DocumentName,
      DocumentIssued: isOther ? {} : this.props.DocumentIssued
    });
  };

  derivedAlienRegistrationNumberRequired = () => this.props.required
    && !validGenericTextfield(this.props.PermanentResidentCardNumber)
    && !validGenericTextfield(this.props.CertificateNumber);

  derivedPermanentResidentCardNumberRequired = () => this.props.required
    && !validGenericTextfield(this.props.AlienRegistrationNumber)
    && !validGenericTextfield(this.props.CertificateNumber);

  derivedCertificateNumberRequired = () => this.props.required
    && !validGenericTextfield(this.props.AlienRegistrationNumber)
    && !validGenericTextfield(this.props.PermanentResidentCardNumber);

  render() {
    const {
      CitizenshipStatus,
      AbroadDocumentation,
      Explanation,
      DocumentNumber,
      DocumentIssued,
      PlaceIssued,
      DocumentName,
      CertificateNumber,
      CertificateIssued,
      CertificateName,
      CertificateMotherMaidenName,
      hasValidUSPassport,
      formType,
      formVersion,
    } = this.props;

    const data = {
      citizenshipstatus: CitizenshipStatus?.value,
      abroadDocumentation: AbroadDocumentation?.value,
      explanation: Explanation || {},
      documentNumber: DocumentNumber,
      documentIssued: DocumentIssued,
      placeIssued: PlaceIssued,
      documentName: DocumentName,
      certificateNumber: CertificateNumber,
      certificateIssued: CertificateIssued,
      certificateName: CertificateName,
      certificateMotherMaidenName: CertificateMotherMaidenName,
    };

    const resultIsCertificateRequired = isCertificateRequired(data, !hasValidUSPassport);
    const resultIsDocumentRequired = isDocumentRequired(data, !hasValidUSPassport);
    const displayDocumentOther = this.props.AbroadDocumentation?.value === 'Other' && (
      !valueIsEmpty(this.props.DocumentNumber)
      || !dateIsEmpty(this.props.DocumentIssued)
      || (countryString(this.props.PlaceIssued?.country) === 'United States'
        ? !locationCityStateIsEmpty(this.props.PlaceIssued)
        : !locationCityCountryIsEmpty(this.props.PlaceIssued, true))
      || !nameIsEmpty(this.props.DocumentName)
    );

    // create the options for the drop down
    const residenceStatuses = residencyStatusOptions.map((residenceStatus, index) => (
      <option key={`${residenceStatus}_${index}`} value={residenceStatus}>
        {residenceStatus}
      </option>
    ));

    const notCitizenDocumentOptions = notCitizenDocumentTypes.map((documentType, index) => (
      <option key={`${documentType}_${index}`} value={documentType}>
        {documentType}
      </option>
    ));

    const getCitizenshipLabel = (option: string): string => {
      switch (option) {
        case PVQ_CITIZEN:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.citizen');
        case PVQ_FOREIGN_BORN:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.foreignborn');
        case PVQ_NATURALIZED:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.naturalized');
        case PVQ_DERIVED:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.derived');
        case PVQ_NOT_CITIZEN:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.notcitizen');
        case PVQ_NATIONAL_BY_BIRTH_IN_US:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.nationalByBirthInUs');
        case PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US:
          return i18n.t('citizenship.pvq.status.label.citizenshipstatus.nationalByBirthOutsideUS');
        default:
          return '';
      }
    };

    return isPVQForm(formType) ? (
      <div
        className="section-content status"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_STATUS.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.destination.status')}</h1>
        <h4 className="bottom-spacing">{i18n.t('citizenship.pvq.status.intro.info.text')}</h4>
        <Field
          title={i18n.t('citizenship.pvq.status.heading.citizenshipstatus')}
          adjustFor="buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute="CitizenshipStatus"
        >
          <RadioGroup
            className="citizenship-status option-list option-list-vertical"
            required={this.props.required}
            onError={this.handleError}
            selectedValue={this.props.CitizenshipStatus?.value}
            attribute="CitizenshipStatus"
          >
            {pvqCitizenshipStatusOptions.map((option) => (
              <Radio
                name={option}
                label={getCitizenshipLabel(option)}
                value={option}
                onUpdate={this.updateCitizenshipStatus}
                onError={this.handleError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.CitizenshipStatus?.value === PVQ_FOREIGN_BORN && (
          <div>
            <div>
              <h3 className="section-header">{i18n.t('citizenship.pvq.status.heading.foreignBorn.document')}</h3>
              <Field
                title={i18n.t('citizenship.pvq.status.heading.abroad')}
                adjustFor="buttons"
                scrollIntoView={this.props.scrollIntoView}
                attribute="AbroadDocumentation"
              >
                <RadioGroup
                  className="citizenship-abroad"
                  required={resultIsDocumentRequired && this.props.required}
                  onError={this.handleError}
                  selectedValue={this.props.AbroadDocumentation?.value}
                  attribute="AbroadDocumentation"
                >
                  <Radio
                    name="citizenship-abroad-fs240"
                    label={i18n.t('citizenship.pvq.status.label.abroad.fs240')}
                    value="FS-240"
                    className="citizenship-abroad-fs240"
                    onUpdate={(value) => {
                      this.updateAbroadDocumentation(value);
                    }}
                    onError={this.handleError}
                  />
                  <Radio
                    name="citizenship-abroad-ds1350"
                    label={i18n.t('citizenship.pvq.status.label.abroad.ds1350')}
                    value="DS-1350"
                    className="citizenship-abroad-ds1350"
                    onUpdate={(value) => {
                      this.updateAbroadDocumentation(value);
                    }}
                    onError={this.handleError}
                  />
                  <Radio
                    name="citizenship-abroad-fs545"
                    label={i18n.t('citizenship.pvq.status.label.abroad.fs545')}
                    value="FS-545"
                    className="citizenship-abroad-fs545"
                    onUpdate={(value) => {
                      this.updateAbroadDocumentation(value);
                    }}
                    onError={this.handleError}
                  />
                  <Radio
                    name="citizenship-abroad-n560-or-n561"
                    label={i18n.t('citizenship.pvq.status.label.abroad.n560')}
                    value="N-560 or N-561"
                    className="citizenship-abroad-n560-or-n561"
                    onUpdate={(value) => {
                      this.updateAbroadDocumentation(value);
                    }}
                    onError={this.handleError}
                  />
                  <Radio
                    name="citizenship-abroad-other"
                    label={i18n.t('citizenship.pvq.status.label.abroad.other')}
                    value="Other"
                    className="citizenship-abroad-other"
                    onUpdate={(value) => {
                      this.updateAbroadDocumentation(value);
                    }}
                    onError={this.handleError}
                  />
                </RadioGroup>

                {this.props.AbroadDocumentation?.value === 'Other' && (
                  <Field
                    title={i18n.t('citizenship.pvq.status.label.documentTypeOtherExplanation')}
                    titleSize="label"
                    attribute="DocumentTitle"
                    adjustFor="textarea"
                  >
                    <Textarea
                      attribute="DocumentTitle"
                      name="DocumentTitle"
                      className="citizenship-abroad-document-title"
                      {...this.props.DocumentTitle}
                      onUpdate={(value) => {
                        this.updateField('DocumentTitle', value);
                      }}
                      onError={this.handleError}
                      required={resultIsDocumentRequired && this.props.required}
                    />
                  </Field>
                )}
              </Field>
              <Field
                title={i18n.t('citizenship.pvq.status.heading.documentnumber.foreignborn')}
                scrollIntoView={this.props.scrollIntoView}
                attribute="DocumentNumber"
                help="citizenship.pvq.status.help.documentnumber.foreignborn"
              >
                <NotApplicable
                  name="DocumentNumberNotApplicable"
                  {...this.props.DocumentNumberNotApplicable}
                  label={i18n.t('citizenship.label.notApplicable')}
                  or={i18n.t('citizenship.label.or')}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                  attribute="DocumentNumberNotApplicable"
                  includeNAConfirmation={Boolean(this.props.DocumentNumber?.value)}
                >
                  <Text
                    name="DocumentNumber"
                    className="document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    {...this.props.DocumentNumber}
                    onUpdate={(value) => {
                      this.updateField('DocumentNumber', value);
                    }}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="DocumentNumber"
                  />
                </NotApplicable>
              </Field>

              <div>
                <Field
                  title={i18n.t('citizenship.pvq.status.heading.documentissued')}
                  adjustFor="datecontrol"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentIssued"
                >
                  <DateControl
                    name="DocumentIssued"
                    className="document-issued"
                    minDateEqualTo
                    {...this.props.DocumentIssued}
                    onUpdate={(value) => {
                      this.updateField('DocumentIssued', value);
                    }}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="DocumentIssued"
                  />
                </Field>

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.placeissued')}
                  adjustFor="label"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="PlaceIssued"
                >
                  <PVQLocation
                    name="PlaceIssued"
                    {...this.props.PlaceIssued}
                    foreignTitle={i18n.t('citizenship.pvq.status.heading.whereIssued')}
                    layout={PVQLocation.INTERNATIONAL_CITY_COUNTRY_ONLY}
                    className="place-issued"
                    onUpdate={this.updatePlaceIssued}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="PlaceIssued"
                  />
                </Field>

                <Field
                  // What is your name on this document?
                  title={i18n.t('citizenship.pvq.status.heading.documentname')}
                  optional
                  filterErrors={Name.requiredErrorsOnly}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <PVQName
                    name="DocumentName"
                    aria-label="Enter the name in which document was issued"
                    className="document-name"
                    {...this.props.DocumentName}
                    onUpdate={(value) => {
                      this.updateField('DocumentName', value);
                    }}
                    onError={this.handleNameError} // eslint-disable-line @typescript-eslint/unbound-method
                    required={resultIsDocumentRequired && this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="DocumentName"
                    showLastFirstMiddleNameOrder
                  />
                </Field>
              </div>
            </div>

            <Branch
              name="born_on_military_installation"
              label={i18n.t('citizenship.pvq.status.heading.bornonmilitaryinstallation')}
              labelSize="h4"
              className="born-on-military-installation"
              {...this.props.BornOnMilitaryInstallation}
              onUpdate={(value) => {
                this.updateField('BornOnMilitaryInstallation', value);
              }}
              onError={this.handleError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="BornOnMilitaryInstallation"
            />

            {this.props.BornOnMilitaryInstallation?.value === 'Yes' && (
              <Field
                title={i18n.t('citizenship.pvq.status.heading.militarybase')}
                scrollIntoView={this.props.scrollIntoView}
                attribute="MilitaryBase"
              >
                <Text
                  name="MilitaryBase"
                  className="military-base"
                  {...this.props.MilitaryBase}
                  onUpdate={(value) => {
                    this.updateField('MilitaryBase', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="MilitaryBase"
                />
              </Field>
            )}
          </div>
        )}

        {this.props.CitizenshipStatus?.value === PVQ_NATURALIZED && (
          <div>
            <Field
              title={i18n.t('citizenship.pvq.status.heading.alienregistrationnumber.naturalized')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="AlienRegistrationNumber"
              help="citizenship.pvq.status.help.alienregistrationnumber.naturalized"
            >
              <Text
                name="AlienRegistrationNumber"
                className="alien-registration-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.AlienRegistrationNumber}
                onUpdate={(value) => {
                  this.updateField('AlienRegistrationNumber', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="AlienRegistrationNumber"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.pvq.status.heading.certificatenumber.naturalized')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="CertificateNumber"
              help="citizenship.pvq.status.help.certificatenumber.naturalized"
            >
              <Text
                name="CertificateNumber"
                className="certificate-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.CertificateNumber}
                onUpdate={(value) => {
                  this.updateField('CertificateNumber', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateNumber"
              />
            </Field>
            <Field
              title={i18n.t('citizenship.pvq.status.heading.certificateissued.naturalized')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="CertificateIssued"
            >
              <DateControl
                name="CertificateIssued"
                className="certificate-issued"
                {...this.props.CertificateIssued}
                minDateEqualTo
                onUpdate={(value) => {
                  this.updateField('CertificateIssued', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateIssued"
                showEstimated={false}
              />
            </Field>

            <Field
              // What is your name on your Certificate of Naturalization?
              title={i18n.t('citizenship.pvq.status.heading.certificatename.naturalized')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <PVQName
                name="CertificateName"
                aria-label="Provide the name in which the Certificate of Naturalization was issued"
                className="certificate-name"
                {...this.props.CertificateName}
                onUpdate={(value) => {
                  this.updateField('CertificateName', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute="CertificateName"
                hideNoLastName
                showLastFirstMiddleNameOrder
              />
            </Field>
          </div>
        )}

        {this.props.CitizenshipStatus?.value === PVQ_DERIVED && (
          <div>
            <Field
              title={i18n.t('citizenship.pvq.status.heading.alienregistrationnumber.derived')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="AlienRegistrationNumber"
              help="citizenship.pvq.status.help.alienregistrationnumber.derived"
            >
              <Text
                name="AlienRegistrationNumber"
                className="alien-registration-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.AlienRegistrationNumber}
                onUpdate={(value) => {
                  this.updateField('AlienRegistrationNumber', value);
                }}
                onError={this.handleError}
                required={this.derivedAlienRegistrationNumberRequired()}
                attribute="AlienRegistrationNumber"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.pvq.status.heading.permanentresidentcardnumber')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="PermanentResidentCardNumber"
            >

              <Text
                name="PermanentResidentCardNumber"
                className="permanent-resident-card-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.PermanentResidentCardNumber}
                onUpdate={(value) => {
                  this.updateField('PermanentResidentCardNumber', value);
                }}
                onError={this.handleError}
                required={this.derivedPermanentResidentCardNumberRequired()}
                attribute="PermanentResidentCardNumber"
              />

            </Field>

            <Field
              title={i18n.t('citizenship.pvq.status.heading.certificatenumber.derived')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="CertificateNumber"
              help="citizenship.pvq.status.help.certificatenumber.derived"
            >
              <NotApplicable // moved this from permantresidentcardnumber as its in the wrong place - TODO change the variable names and check saves
                name="CitizenshipCertificateCardNumberNotApplicable"
                {...this.props.CitizenshipCertificateCardNumberNotApplicable}
                label={i18n.t('citizenship.label.notApplicable')}
                or={i18n.t('citizenship.label.or')}
                onUpdate={this.updateCitizenshipCertificateCardNumberNotApplicable}
                attribute="CitizenshipCertificateCardNumberNotApplicable"
                includeNAConfirmation={Boolean(this.props.CertificateNumber?.value)} //
              >
                <Text
                  name="CertificateNumber"
                  className="certificate-number"
                  {...this.props.CertificateNumber}
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  onUpdate={(value) => {
                    this.updateField('CertificateNumber', value);
                  }}
                  onError={this.handleError}
                  required={this.derivedCertificateNumberRequired()}
                  attribute="CertificateNumber"
                />
              </NotApplicable>
            </Field>

            <Field
              // What is your name on your Certificate of Citizenship?
              title={i18n.t('citizenship.pvq.status.heading.certificatename.derived')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <PVQName
                name="CertificateName"
                aria-label="Provide the name in which the certificate was issued"
                className="certificate-name"
                {...this.props.CertificateName}
                onUpdate={(value) => {
                  this.updateField('CertificateName', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateName"
                hideNoLastName
                showLastFirstMiddleNameOrder
              />
            </Field>

            <Field
              title={i18n.t('citizenship.pvq.status.heading.certificateissued.derived')}
              help="citizenship.pvq.status.help.certificateissued"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="CertificateIssued"
            >
              <DateControl
                name="CertificateIssued"
                className="certificate-issued"
                {...this.props.CertificateIssued}
                minDateEqualTo
                onUpdate={(value) => {
                  this.updateField('CertificateIssued', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateIssued"
                showEstimated={false}
              />
            </Field>

          </div>
        )}

        {isNationalOrNonCitizen(this.props.CitizenshipStatus?.value) && (
          <div>
            <Field
              title={i18n.t('citizenship.pvq.status.heading.entrydate')}
              help="citizenship.pvq.status.help.entrydate"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="EntryDate"
              exactAttribute
            >
              <NotApplicable
                name="EntryDateNotApplicable"
                {...this.props.EntryDateNotApplicable}
                label={i18n.t('citizenship.label.notApplicable')}
                or={i18n.t('citizenship.label.or')}
                onUpdate={this.updateEntryDateNotApplicable}
                attribute="EntryDateNotApplicable"
                includeNAConfirmation={Boolean(this.props.EntryDate?.value)}
              >
                <DateControl
                  name="EntryDate"
                  className="entry-date"
                  {...this.props.EntryDate}
                  minDateEqualTo
                  onUpdate={(value) => {
                    this.updateField('EntryDate', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="EntryDate"
                />
              </NotApplicable>
            </Field>

            {this.props.EntryDateNotApplicable?.applicable === true && (
              <Field
                title={i18n.t('citizenship.pvq.status.heading.entrylocation')}
                adjustFor="address"
                scrollIntoView={this.props.scrollIntoView}
                attribute="EntryLocation"
              >
                <PVQLocation
                  name="EntryLocation"
                  className="entry-location"
                  {...this.props.EntryLocation}
                  layout={Location.CITY_STATE}
                  onUpdate={(value) => {
                    this.updateField('EntryLocation', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="EntryLocation"
                />
              </Field>
            )}

            <Field
              title={i18n.t('citizenship.pvq.status.heading.priorcitizenship.notcitizen')}
              help="citizenship.pvq.status.help.priorcitizenship"
              scrollIntoView={this.props.scrollIntoView}
              attribute="PriorCitizenship"
            >
              <Country
                name="PriorCitizenship"
                className="prior-citizenship"
                {...this.props.PriorCitizenship}
                multiple
                onUpdate={(value) => {
                  this.updateField('PriorCitizenship', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="PriorCitizenship"
                excludeUnitedStates
              />
            </Field>

            <Branch
              {...this.props.IsResidentOfUnitedStates}
              name="IsResidentOfUnitedStates"
              attribute="IsResidentOfUnitedStates"
              label={i18n.t('citizenship.pvq.status.heading.isResidentOfUnitedStates')}
              labelSize="h4"
              className="isResidentOfUnitedStates"
              warning
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              onUpdate={this.updateIsResidentOfUnitedStates}
              scrollIntoView={this.props.scrollIntoView}
            />

            {this.props.IsResidentOfUnitedStates?.value === 'Yes' && (
              <>
                <Field
                  title={i18n.t('citizenship.pvq.status.heading.residencestatus')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="ResidenceStatus"
                >
                  <Dropdown
                    attribute="ResidenceStatus"
                    placeholder={this.props.ResidenceStatus?.value}
                    onUpdate={(value) => {
                      this.updateField('ResidenceStatus', value);
                    }}
                    onError={this.props.onError}
                    sort={false}
                    value={this.props.ResidenceStatus?.value}
                    required={this.props.required}
                  >
                    {residenceStatuses}
                  </Dropdown>
                </Field>

                {this.props.ResidenceStatus?.value === 'Other' && (
                  <Field
                    title={i18n.t('citizenship.pvq.status.label.explanation')}
                    titleSize="label"
                    adjustFor="textarea"
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="ResidenceStatusExplanation"
                  >
                    <Textarea
                      name="ResidenceStatusExplanation"
                      className="citizenship-document-type-explanation"
                      {...this.props.ResidenceStatusExplanation}
                      onUpdate={(value) => {
                        this.updateField('ResidenceStatusExplanation', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="ResidenceStatusExplanation"
                    />
                  </Field>
                )}

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.alienregistrationnumber.notcitizen')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="AlienRegistrationNumber"
                  help="citizenship.pvq.status.help.alienregistrationnumber.notcitizen"
                >
                  <NotApplicable
                    name="AlienRegistrationNumberNotApplicable"
                    {...this.props.AlienRegistrationNumberNotApplicable}
                    label={i18n.t('citizenship.label.notApplicable')}
                    or={i18n.t('citizenship.label.or')}
                    onUpdate={this.updateAlienRegistrationNumberNotApplicable}
                    attribute="AlienRegistrationNumberNotApplicable"
                    includeNAConfirmation={Boolean(this.props.AlienRegistrationNumber?.value)}
                  >
                    <Text
                      name="AlienRegistrationNumber"
                      className="alien-registration-number"
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      {...this.props.AlienRegistrationNumber}
                      onUpdate={(value) => {
                        this.updateField('AlienRegistrationNumber', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="AlienRegistrationNumber"
                    />
                  </NotApplicable>
                </Field>

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.documentexpiration')}
                  adjustFor="datecontrol"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentExpiration"
                >
                  <NotApplicable
                    name="DocumentExpirationNotApplicable"
                    {...this.props.DocumentExpirationNotApplicable}
                    label={i18n.t('citizenship.label.notApplicable')}
                    or={i18n.t('citizenship.label.or')}
                    onUpdate={this.updateDocumentExpirationNotApplicable}
                    attribute="DocumentExpirationNotApplicable"
                    includeNAConfirmation={Boolean(this.props.DocumentExpiration
                      && (this.props.DocumentExpiration.day
                        || this.props.DocumentExpiration.month
                        || this.props.DocumentExpiration.year
                      ))}
                  >
                    <DateControl
                      name="DocumentExpiration"
                      className="document-expiration"
                      {...this.props.DocumentExpiration}
                      noMaxDate
                      minDateEqualTo
                      onUpdate={(value) => {
                        this.updateField('DocumentExpiration', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="DocumentExpiration"
                      showEstimated={false}
                    />
                  </NotApplicable>
                </Field>

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.documenttype')}
                  adjustFor="buttons"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentType"
                >
                  <Dropdown
                    attribute="DocumentType"
                    placeholder={this.props.DocumentType?.value}
                    onUpdate={(value) => {
                      this.updateField('DocumentType', value);
                    }}
                    onError={this.props.onError}
                    sort={false}
                    value={this.props.DocumentType?.value}
                    required={this.props.required}
                  >
                    {notCitizenDocumentOptions}
                  </Dropdown>

                  {this.props.DocumentType?.value === 'Other' && (
                    <Field
                      title={i18n.t('citizenship.pvq.status.label.documentTypeOtherExplanation')}
                      titleSize="label"
                      adjustFor="textarea"
                      scrollIntoView={this.props.scrollIntoView}
                      attribute="DocumentTitle"
                    >
                      <Text
                        name="DocumentTitle"
                        className="citizenship-document-type-title"
                        {...this.props.DocumentTitle}
                        onUpdate={(value) => {
                          this.updateField('DocumentTitle', value);
                        }}
                        onError={this.handleError}
                        required={this.props.required}
                        attribute="DocumentTitle"
                      />
                    </Field>
                  )}
                </Field>

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.documentnumber.notcitizen')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentNumber"
                  help="citizenship.pvq.status.help.documentnumber.notcitizen"
                >
                  <NotApplicable
                    name="DocumentNumberNotApplicable"
                    {...this.props.DocumentNumberNotApplicable}
                    label={i18n.t('citizenship.label.notApplicable')}
                    or={i18n.t('citizenship.label.or')}
                    onUpdate={this.updateDocumentNumberNotApplicable}
                    attribute="DocumentNumberNotApplicable"
                    includeNAConfirmation={Boolean(this.props.DocumentNumber?.value)}
                  >
                    <Text
                      name="DocumentNumber"
                      className="document-number"
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      {...this.props.DocumentNumber}
                      onUpdate={(value) => {
                        this.updateField('DocumentNumber', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="DocumentNumber"
                    />
                  </NotApplicable>
                </Field>
                <Field
                  title={i18n.t('citizenship.pvq.status.heading.documentissued')}
                  adjustFor="datecontrol"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentIssued"
                >
                  <DateControl
                    name="DocumentIssued"
                    className="document-issued"
                    {...this.props.DocumentIssued}
                    minDateEqualTo
                    onUpdate={(value) => {
                      this.updateField('DocumentIssued', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="DocumentIssued"
                    showEstimated={false}
                  />
                </Field>

                <Field
                  title={i18n.t('citizenship.pvq.status.heading.alienregistrationexpiration')}
                  adjustFor="datecontrol"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="AlienRegistrationExpiration"
                >
                  <DateControl
                    name="AlienRegistrationExpiration"
                    className="alien-registration-expiration"
                    {...this.props.AlienRegistrationExpiration}
                    noMaxDate
                    onUpdate={(value) => {
                      this.updateField('AlienRegistrationExpiration', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="AlienRegistrationExpiration"
                    showEstimated={false}
                  />
                </Field>

                <Field
                  // What is your name on this document?
                  title={i18n.t('citizenship.pvq.status.heading.documentname')}
                  filterErrors={Name.requiredErrorsOnly}
                  optional
                >
                  <PVQName
                    name="DocumentName"
                    aria-label="Provide the name in which the document was issued"
                    className="document-name"
                    {...this.props.DocumentName}
                    onUpdate={(value) => {
                      this.updateField('DocumentName', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="DocumentName"
                    hideNoLastName
                    showLastFirstMiddleNameOrder
                  />
                </Field>
              </>
            )}
          </div>
        )}
      </div>
    ) : (
      <div
        className="section-content status"
        data-section={CITIZENSHIP.key}
        data-subsection={CITIZENSHIP_STATUS.key}
      >
        <h1 className="section-header">{i18n.t('citizenship.destination.status')}</h1>
        <Field
          title={i18n.t('citizenship.status.heading.citizenshipstatus')}
          adjustFor="buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute="CitizenshipStatus"
        >
          {/* eslint jsx-a11y/label-has-associated-control: 0 */}
          {/* eslint jsx-a11y/label-has-for: 0 */}
          <label id="citizenship-status">
            {i18n.t('citizenship.status.heading.citizenshipstatusLabel')}
          </label>
          <RadioGroup
            className="citizenship-status option-list option-list-vertical"
            required={this.props.required}
            onError={this.handleError}
            selectedValue={this.props.CitizenshipStatus?.value}
            attribute="CitizenshipStatus"
          >
            <Radio
              name="citizenship-status-citizen"
              label={i18n.t('citizenship.status.label.citizenshipstatus.citizen')}
              value="Citizen"
              className="citizenship-status-citizen"
              onUpdate={this.updateCitizenshipStatus}
              onError={this.handleError}
            />
            {this.props.requireCitizenshipStatusForeignBorn && (
              <Radio
                name="citizenship-status-foreignborn"
                label={i18n.t('citizenship.status.label.citizenshipstatus.foreignborn')}
                value="ForeignBorn"
                className="citizenship-status-foreignborn"
                onUpdate={this.updateCitizenshipStatus}
                onError={this.handleError}
              />
            )}
            <Radio
              name="citizenship-status-naturalized"
              label={
                !this.props.requireCitizenshipStatusForeignBorn
                  ? i18n.t('citizenship.status.label.citizenshipstatus.foreignborn2013')
                  : i18n.t('citizenship.status.label.citizenshipstatus.naturalized')
              }
              value="Naturalized"
              className="citizenship-status-naturalized"
              onUpdate={this.updateCitizenshipStatus}
              onError={this.handleError}
            />
            {this.props.requireCitizenshipStatusDerived && (
              <Radio
                name="citizenship-status-derived"
                label={i18n.t('citizenship.status.label.citizenshipstatus.derived')}
                value="Derived"
                className="citizenship-status-derived"
                onUpdate={this.updateCitizenshipStatus}
                onError={this.handleError}
              />
            )}
            <Radio
              name="citizenship-status-notcitizen"
              label={i18n.t('citizenship.status.label.citizenshipstatus.notcitizen')}
              value="NotCitizen"
              className="citizenship-status-notcitizen"
              onUpdate={this.updateCitizenshipStatus}
              onError={this.handleError}
            />
          </RadioGroup>
        </Field>
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('citizenship.comments.branch')}
            name="CitizenshipStatusComments"
            className="status-comments"
            attribute="CitizenshipStatusComments"
            {...this.props.CitizenshipStatusComments}
            onUpdate={this.updateCitizenshipStatusComments}
            required={false}
          />
        )}

        {this.props.CitizenshipStatus?.value === 'ForeignBorn'
          && this.props.requireCitizenshipStatusForeignBorn && (
            <div>
              {!hasValidUSPassport && (
                <div>
                  <h3 className="section-header">{i18n.t('citizenship.status.heading.foreignBorn.document')}</h3>
                  <Field
                    title={i18n.t('citizenship.status.heading.abroad')}
                    adjustFor="buttons"
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="AbroadDocumentation"
                  >
                    <RadioGroup
                      className="citizenship-abroad"
                      required={resultIsDocumentRequired && this.props.required}
                      onError={this.handleError}
                      selectedValue={this.props.AbroadDocumentation?.value}
                      attribute="AbroadDocumentation"
                    >
                      <Radio
                        name="citizenship-abroad-fs240"
                        label={i18n.t('citizenship.status.label.abroad.fs240')}
                        value="FS-240"
                        className="citizenship-abroad-fs240"
                        onUpdate={(value) => {
                          this.updateAbroadDocumentation(value);
                        }}
                        onError={this.handleError}
                      />
                      <Radio
                        name="citizenship-abroad-ds1350"
                        label={i18n.t('citizenship.status.label.abroad.ds1350')}
                        value="DS-1350"
                        className="citizenship-abroad-ds1350"
                        onUpdate={(value) => {
                          this.updateAbroadDocumentation(value);
                        }}
                        onError={this.handleError}
                      />
                      <Radio
                        name="citizenship-abroad-fs545"
                        label={i18n.t('citizenship.status.label.abroad.fs545')}
                        value="FS-545"
                        className="citizenship-abroad-fs545"
                        onUpdate={(value) => {
                          this.updateAbroadDocumentation(value);
                        }}
                        onError={this.handleError}
                      />
                      <Radio
                        name="citizenship-abroad-other"
                        label={i18n.t('citizenship.status.label.abroad.other')}
                        value="Other"
                        className="citizenship-abroad-other"
                        onUpdate={(value) => {
                          this.updateAbroadDocumentation(value);
                        }}
                        onError={this.handleError}
                      />
                    </RadioGroup>

                    {this.props.AbroadDocumentation?.value === 'Other' && (
                      <Field
                        title={i18n.t('citizenship.status.label.explanation')}
                        titleSize="label"
                        attribute="DocumentTitle"
                        adjustFor="textarea"
                      >
                        <Textarea
                          attribute="DocumentTitle"
                          name="Explanation"
                          className="citizenship-abroad-document-title"
                          {...this.props.DocumentTitle}
                          onUpdate={(value) => {
                            this.updateField('DocumentTitle', value);
                          }}
                          onError={this.handleError}
                          required={resultIsDocumentRequired && this.props.required}
                        />
                      </Field>
                    )}
                  </Field>
                  <Field
                    title={i18n.t('citizenship.status.heading.documentnumber.foreignborn')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="DocumentNumber"
                  >
                    <Text
                      name="DocumentNumber"
                      className="document-number"
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      {...this.props.DocumentNumber}
                      onUpdate={(value) => {
                        this.updateField('DocumentNumber', value);
                      }}
                      onError={this.handleError}
                      required={resultIsDocumentRequired && this.props.required}
                      attribute="DocumentNumber"
                    />
                  </Field>

                  {(this.props.AbroadDocumentation?.value !== 'Other' || displayDocumentOther) && (
                    <div>
                      <Field
                        title={i18n.t('citizenship.status.heading.documentissued')}
                        adjustFor="datecontrol"
                        scrollIntoView={this.props.scrollIntoView}
                        attribute="DocumentIssued"
                      >
                        <DateControl
                          name="DocumentIssued"
                          className="document-issued"
                          minDateEqualTo
                          {...this.props.DocumentIssued}
                          onUpdate={(value) => {
                            this.updateField('DocumentIssued', value);
                          }}
                          onError={this.handleError}
                          required={resultIsDocumentRequired && this.props.required}
                          attribute="DocumentIssued"
                        />
                      </Field>

                      <Field
                        title={i18n.t('citizenship.status.heading.placeissued')}
                        adjustFor="label"
                        scrollIntoView={this.props.scrollIntoView}
                        attribute="PlaceIssued"
                      >
                        <Location
                          name="PlaceIssued"
                          {...this.props.PlaceIssued}
                          layout={Location.BIRTHPLACE_WITHOUT_COUNTY}
                          className="place-issued"
                          onUpdate={this.updatePlaceIssued}
                          onError={this.handleError}
                          required={resultIsDocumentRequired && this.props.required}
                          attribute="PlaceIssued"
                        />
                      </Field>

                      <Field
                        title={i18n.t('citizenship.status.heading.documentname')}
                        optional
                        filterErrors={Name.requiredErrorsOnly}
                        scrollIntoView={this.props.scrollIntoView}
                      >
                        <Name
                          name="DocumentName"
                          aria-label="Enter the name in which document was issued"
                          className="document-name"
                          {...this.props.DocumentName}
                          onUpdate={(value) => {
                            this.updateField('DocumentName', value);
                          }}
                          onError={this.handleNameError} // eslint-disable-line @typescript-eslint/unbound-method
                          required={resultIsDocumentRequired && this.props.required}
                          scrollIntoView={this.props.scrollIntoView}
                          attribute="DocumentName"
                        />
                      </Field>
                    </div>
                  )}

                  <h3 className="section-header">{i18n.t('citizenship.status.heading.foreignBorn.citizenship')}</h3>
                  <Field
                    title={i18n.t('citizenship.status.heading.certificatenumber.foreignborn')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="CertificateNumber"
                  >
                    <Text
                      name="CertificateNumber"
                      className="certificate-number"
                      {...this.props.CertificateNumber}
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      onUpdate={(value) => {
                        this.updateField('CertificateNumber', value);
                      }}
                      onError={this.handleError}
                      required={resultIsCertificateRequired && this.props.required}
                      attribute="CertificateNumber"
                    />
                  </Field>

                  <Field
                    title={i18n.t('citizenship.status.heading.certificateissued.foreignborn')}
                    adjustFor="datecontrol"
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="CertificateIssued"
                  >
                    <DateControl
                      name="CertificateIssued"
                      className="certificate-issued"
                      {...this.props.CertificateIssued}
                      minDateEqualTo
                      onUpdate={(value) => {
                        this.updateField('CertificateIssued', value);
                      }}
                      onError={this.handleError}
                      required={resultIsCertificateRequired && this.props.required}
                      attribute="CertificateIssued"
                    />
                  </Field>

                  <Field
                    title={i18n.t('citizenship.status.heading.certificatename.foreignborn')}
                    optional
                    filterErrors={Name.requiredErrorsOnly}
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <Name
                      name="CertificateName"
                      aria-label="Enter the name in which the certificate was issued"
                      className="certificate-name"
                      {...this.props.CertificateName}
                      onUpdate={(value) => {
                        this.updateField('CertificateName', value);
                      }}
                      onError={this.handleError}
                      required={resultIsCertificateRequired && this.props.required}
                      attribute="CertificateName"
                    />
                  </Field>
                </div>
              )}
              <Branch
                name="born_on_military_installation"
                label={i18n.t('citizenship.status.heading.bornonmilitaryinstallation')}
                labelSize="h4"
                className="born-on-military-installation"
                {...this.props.BornOnMilitaryInstallation}
                onUpdate={(value) => {
                  this.updateField('BornOnMilitaryInstallation', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute="BornOnMilitaryInstallation"
              />
              {!isFormSF852013(formType, formVersion) && (
                <Comments
                  title={i18n.t('citizenship.comments.optional')}
                  name="ProofOfUSCitizenshipComments"
                  className="optional-comment"
                  attribute="ForeignBornProofOfUSCitizenshipComments"
                  {...this.props.ProofOfUSCitizenshipComments}
                  onUpdate={this.updateProofOfUSCitizenshipComments}
                  required={false}
                />
              )}

              {this.props.BornOnMilitaryInstallation?.value === 'Yes' && (
                <>
                  <Field
                    title={i18n.t('citizenship.status.heading.militarybase')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="MilitaryBase"
                  >
                    <Text
                      name="MilitaryBase"
                      className="military-base"
                      {...this.props.MilitaryBase}
                      onUpdate={(value) => {
                        this.updateField('MilitaryBase', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="MilitaryBase"
                    />
                  </Field>
                  {!isFormSF852013(formType, formVersion) && (
                    <Comments
                      title={i18n.t('citizenship.comments.optional')}
                      name="MilitaryBaseComments"
                      className="optional-comment"
                      attribute="ForeignBornMilitaryBaseComments"
                      {...this.props.MilitaryBaseComments}
                      onUpdate={this.updateMilitaryBaseComments}
                      required={false}
                    />
                  )}
                </>
              )}
            </div>
          )}

        {this.props.CitizenshipStatus?.value && this.props.requireCitizenshipStatusMaidenName && (
          <Field
            title={i18n.t('citizenship.status.heading.maidenName')}
            scrollIntoView={this.props.scrollIntoView}
            attribute="MaidenName"
          >
            <Text
              name="MaidenName"
              {...this.props.MaidenName}
              onUpdate={(value) => {
                this.updateField('MaidenName', value);
              }}
              onError={this.handleError}
              attribute="MaidenName"
            />
          </Field>
        )}

        {(this.props.formType === formTypes.SF85 && this.props.formVersion !== '2013-12')
          && (
            <Field
              title={i18n.t('citizenship.status.heading.certificatename.mothermaiden')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Name
                name="CertificateMotherMaidenName"
                aria-label="Provide mother's maiden name"
                className="certificate-mother-maiden-name"
                {...this.props.CertificateMotherMaidenName}
                onUpdate={(value) => {
                  this.updateField('CertificateMotherMaidenName', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateMotherMaidenName"
              />
            </Field>
          )}

        {this.props.CitizenshipStatus?.value === 'Naturalized' && (
          <div>
            {this.props.requireCitizenshipStatusNaturalizedFields && (
              <>
                <Field
                  title={i18n.t('citizenship.status.heading.entrydate')}
                  help="citizenship.status.help.entrydate"
                  scrollIntoView={this.props.scrollIntoView}
                  adjustFor="datecontrol"
                  attribute="EntryDate"
                >
                  <DateControl
                    name="EntryDate"
                    className="entry-date"
                    {...this.props.EntryDate}
                    minDateEqualTo
                    onUpdate={(value) => {
                      this.updateField('EntryDate', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="EntryDate"
                  />
                </Field>
                <Field
                  title={i18n.t('citizenship.status.heading.entrylocation')}
                  adjustFor="address"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="EntryLocation"
                >
                  <Location
                    name="EntryLocation"
                    className="entry-location"
                    {...this.props.EntryLocation}
                    layout={Location.CITY_STATE}
                    onUpdate={(value) => {
                      this.updateField('EntryLocation', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="EntryLocation"
                  />
                </Field>

                <Field
                  title={i18n.t('citizenship.status.heading.priorcitizenship.naturalized')}
                  help="citizenship.status.help.priorcitizenship"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="PriorCitizenship"
                >
                  <Country
                    name="PriorCitizenship"
                    className="prior-citizenship"
                    {...this.props.PriorCitizenship}
                    multiple
                    onUpdate={(value) => {
                      this.updateField('PriorCitizenship', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="PriorCitizenship"
                    excludeUnitedStates
                  />
                </Field>
                <Branch
                  name="has_alien_registration"
                  label={i18n.t('citizenship.status.heading.hasalienregistration')}
                  labelSize="h4"
                  className="has-alien-registration"
                  {...this.props.HasAlienRegistration}
                  onUpdate={(value) => {
                    this.updateField('HasAlienRegistration', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="HasAlienRegistration"
                />
                {!isFormSF852013(formType, formVersion) && (
                  <Comments
                    title={i18n.t('citizenship.comments.optional')}
                    name="NaturalizationComments"
                    className="optional-comment"
                    attribute="NaturalizationComments"
                    {...this.props.NaturalizationComments}
                    onUpdate={this.updateNaturalizationComments}
                    required={false}
                  />
                )}
              </>
            )}

            {this.props.requireCitizenshipStatusNaturalizedFields
              && this.props.HasAlienRegistration?.value === 'Yes' && (
                <>
                  <Field
                    title={i18n.t('citizenship.status.heading.alienregistrationnumber.naturalized')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="AlienRegistrationNumber"
                  >
                    <Text
                      name="AlienRegistrationNumber"
                      className="alien-registration-number"
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      {...this.props.AlienRegistrationNumber}
                      onUpdate={(value) => {
                        this.updateField('AlienRegistrationNumber', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="AlienRegistrationNumber"
                    />
                  </Field>
                  {!isFormSF852013(formType, formVersion) && (
                    <Comments
                      title={i18n.t('citizenship.comments.optional')}
                      name="AlienRegistrationNumberComments"
                      className="optional-comment"
                      attribute="AlienRegistrationNumberComments"
                      {...this.props.AlienRegistrationNumberComments}
                      onUpdate={this.updateAlienRegistrationNumberComments}
                      required={false}
                    />
                  )}
                </>
              )}

            {this.props.requireCitizenshipStatusCitizenshipCertificateFields && (
              <h3 className="section-header">{i18n.t('citizenship.status.heading.naturalized.naturalization')}</h3>
            )}

            <Field
              title={i18n.t('citizenship.status.heading.certificatenumber.naturalized')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="CertificateNumber"
            >
              <Text
                name="CertificateNumber"
                className="certificate-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.CertificateNumber}
                onUpdate={(value) => {
                  this.updateField('CertificateNumber', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateNumber"
              />
            </Field>
            <Field
              title={i18n.t('citizenship.status.heading.certificatecourtname')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="CertificateCourtName"
            >
              <Text
                name="CertificateCourtName"
                className="certificate-court-name"
                {...this.props.CertificateCourtName}
                onUpdate={(value) => {
                  this.updateField('CertificateCourtName', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateCourtName"
              />
            </Field>
            <Field
              title={i18n.t('citizenship.status.heading.certificatecourtaddress')}
              optional
              help="citizenship.status.help.certificatecourtaddress"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="label"
              attribute="CertificateCourtAddress"
            >
              <Location
                name="CertificateCourtAddress"
                {...this.props.CertificateCourtAddress}
                layout={Location.US_ADDRESS}
                geocode
                className="certificate-court-address"
                onUpdate={(value) => {
                  this.updateField('CertificateCourtAddress', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateCourtAddress"
              />
            </Field>
            <Field
              title={i18n.t('citizenship.status.heading.certificateissued.naturalized')}
              help="citizenship.status.help.certificateissued"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="CertificateIssued"
            >
              <DateControl
                name="CertificateIssued"
                className="certificate-issued"
                {...this.props.CertificateIssued}
                minDateEqualTo
                onUpdate={(value) => {
                  this.updateField('CertificateIssued', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateIssued"
              />
            </Field>

            {this.props.requireCitizenshipStatusNaturalizedFields && (
              <Field
                title={i18n.t('citizenship.status.heading.certificatename.naturalized')}
                optional
                filterErrors={Name.requiredErrorsOnly}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Name
                  name="CertificateName"
                  aria-label="Provide the name in which the Certificate of Naturalization was issued"
                  className="certificate-name"
                  {...this.props.CertificateName}
                  onUpdate={(value) => {
                    this.updateField('CertificateName', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="CertificateName"
                />
              </Field>
            )}

            {this.props.requireCitizenshipStatusCitizenshipCertificateFields && (
              <>
                <h3 className="section-header">{i18n.t('citizenship.status.heading.naturalized.citizenship')}</h3>
                <Field
                  title={i18n.t('citizenship.status.heading.certificatenumber.foreignborn')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentNumber"
                >
                  <Text
                    name="DocumentNumber"
                    className="document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    {...this.props.DocumentNumber}
                    onUpdate={(value) => {
                      this.updateField('DocumentNumber', value);
                    }}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="DocumentNumber"
                  />
                </Field>

                <Field
                  title={i18n.t('citizenship.status.heading.certificateissued.foreignborn')}
                  adjustFor="datecontrol"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentIssued"
                >
                  <DateControl
                    name="DocumentIssued"
                    className="document-issued"
                    minDateEqualTo
                    {...this.props.DocumentIssued}
                    onUpdate={(value) => {
                      this.updateField('DocumentIssued', value);
                    }}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="DocumentIssued"
                  />
                </Field>

                <Field
                  title={i18n.t('citizenship.status.heading.placeissued')}
                  adjustFor="label"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="PlaceIssued"
                >
                  <Location
                    name="PlaceIssued"
                    {...this.props.PlaceIssued}
                    layout={Location.CITY_STATE}
                    className="place-issued"
                    onUpdate={this.updatePlaceIssued}
                    onError={this.handleError}
                    required={resultIsDocumentRequired && this.props.required}
                    attribute="PlaceIssued"
                  />
                </Field>
              </>
            )}

            {this.props.requireCitizenshipStatusForm240Date && (
              <>
                <h3 className="section-header">{i18n.t('citizenship.status.heading.naturalized.form240')}</h3>
                <Field
                  title={i18n.t('citizenship.status.heading.form240date')}
                  adjustFor="label"
                  scrollIntoView={this.props.scrollIntoView}
                  exactAttribute
                  attribute="Form240Date"
                >
                  <DateControl
                    name="Form240Date"
                    className="Form240Date"
                    hideDay
                    {...this.props.Form240Date}
                    onUpdate={(value) => {
                      this.updateField('Form240Date', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="Form240Date"
                  />
                </Field>
              </>
            )}
            {this.props.requireCitizenshipStatusForm240DateExplanation && (
              <Field
                title={i18n.t('citizenship.status.heading.form240DateExplanation')}
                scrollIntoView={this.props.scrollIntoView}
                exactAttribute
                attribute="Form240DateExplanation"
              >
                <Textarea
                  name="Form240DateExplanation"
                  className="citizenship-basis-explanation"
                  {...this.props.Form240DateExplanation}
                  onUpdate={(value) => {
                    this.updateField('Form240DateExplanation', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="Form240DateExplanation"
                />
              </Field>
            )}

            {this.props.requireCitizenshipStatusNaturalizedFields && (
              <>
                <Field
                  title={i18n.t('citizenship.status.heading.basis.naturalized')}
                  adjustFor="big-buttons"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="Basis"
                >
                  <RadioGroup
                    className="citizenship-basis option-list option-list-vertical"
                    required={this.props.required}
                    onError={this.handleError}
                    selectedValue={this.props.Basis?.value}
                    attribute="Basis"
                  >
                    <Radio
                      name="citizenship-basis-individual"
                      label={i18n.t('citizenship.status.label.basis.naturalized')}
                      value="Individual"
                      className="citizenship-basis-individual"
                      onUpdate={this.updateBasis}
                      onError={this.handleError}
                    />
                    <Radio
                      name="citizenship-basis-other"
                      label={i18n.t('citizenship.status.label.basis.other')}
                      value="Other"
                      className="citizenship-basis-other"
                      onUpdate={this.updateBasis}
                      onError={this.handleError}
                    />
                  </RadioGroup>

                  {this.props.Basis?.value === 'Other' && (
                    <Field
                      title={i18n.t('citizenship.status.label.explanation')}
                      titleSize="label"
                      adjustFor="textarea"
                      scrollIntoView={this.props.scrollIntoView}
                      attribute="Explanation"
                    >
                      <Textarea
                        name="Explanation"
                        className="citizenship-basis-explanation"
                        {...this.props.Explanation}
                        onUpdate={(value) => {
                          this.updateField('Explanation', value);
                        }}
                        onError={this.handleError}
                        required={this.props.required}
                        attribute="Explanation"
                      />
                    </Field>
                  )}
                </Field>
                {!isFormSF852013(formType, formVersion) && (
                  <Comments
                    title={i18n.t('citizenship.comments.optional')}
                    name="CertificateComments"
                    className="optional-comment"
                    attribute="CertificateComments"
                    {...this.props.CertificateComments}
                    onUpdate={this.updateCertificateComments}
                    required={false}
                  />
                )}
              </>
            )}
          </div>
        )}

        {this.props.CitizenshipStatus?.value === 'Derived' && (
          <div>
            <Field
              title={i18n.t('citizenship.status.heading.alienregistrationnumber.derived')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="AlienRegistrationNumber"
            >
              <Text
                name="AlienRegistrationNumber"
                className="alien-registration-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.AlienRegistrationNumber}
                onUpdate={(value) => {
                  this.updateField('AlienRegistrationNumber', value);
                }}
                onError={this.handleError}
                required={this.derivedAlienRegistrationNumberRequired()}
                attribute="AlienRegistrationNumber"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.permanentresidentcardnumber')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="PermanentResidentCardNumber"
            >
              <Text
                name="PermanentResidentCardNumber"
                className="permanent-resident-card-number"
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                {...this.props.PermanentResidentCardNumber}
                onUpdate={(value) => {
                  this.updateField('PermanentResidentCardNumber', value);
                }}
                onError={this.handleError}
                required={this.derivedPermanentResidentCardNumberRequired()}
                attribute="PermanentResidentCardNumber"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.certificatenumber.derived')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="CertificateNumber"
            >
              <Text
                name="CertificateNumber"
                className="certificate-number"
                {...this.props.CertificateNumber}
                maxlength="30"
                pattern={alphaNumericRegEx}
                prefix="alphanumeric"
                onUpdate={(value) => {
                  this.updateField('CertificateNumber', value);
                }}
                onError={this.handleError}
                required={this.derivedCertificateNumberRequired()}
                attribute="CertificateNumber"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.certificatename.derived')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Name
                name="CertificateName"
                aria-label="Provide the name in which the certificate was issued"
                className="certificate-name"
                {...this.props.CertificateName}
                onUpdate={(value) => {
                  this.updateField('CertificateName', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateName"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.certificateissued.derived')}
              help="citizenship.status.help.certificateissued"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="CertificateIssued"
            >
              <DateControl
                name="CertificateIssued"
                className="certificate-issued"
                {...this.props.CertificateIssued}
                minDateEqualTo
                onUpdate={(value) => {
                  this.updateField('CertificateIssued', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="CertificateIssued"
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.basis.derived')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="big-buttons"
              attribute="Basis"
            >
              <RadioGroup
                className="citizenship-basis option-list option-list-vertical"
                required={this.props.required}
                onError={this.props.onError}
                selectedValue={this.props.Basis?.value}
                attribute="Basis"
              >
                <Radio
                  name="citizenship-basis-individual"
                  label={i18n.t('citizenship.status.label.basis.derived')}
                  value="Individual"
                  className="citizenship-basis-individual"
                  onUpdate={(value) => {
                    this.updateField('Basis', value);
                  }}
                  onError={this.handleError}
                />
                <Radio
                  name="citizenship-basis-other"
                  label={i18n.t('citizenship.status.label.basis.other')}
                  value="Other"
                  className="citizenship-basis-other"
                  onUpdate={(value) => {
                    this.updateField('Basis', value);
                  }}
                  onError={this.handleError}
                />
              </RadioGroup>

              {this.props.Basis?.value === 'Other' && (
                <Field
                  title={i18n.t('citizenship.status.label.explanation')}
                  titleSize="label"
                  adjustFor="textarea"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="Explanation"
                >
                  <Textarea
                    name="Explanation"
                    className="citizenship-basis-explanation"
                    {...this.props.Explanation}
                    onUpdate={(value) => {
                      this.updateField('Explanation', value);
                    }}
                    onError={this.handleError}
                    required={this.props.required}
                    attribute="Explanation"
                  />
                </Field>
              )}
            </Field>
            {!isFormSF852013(formType, formVersion) && (
              <Comments
                title={i18n.t('citizenship.comments.optional')}
                name="DerivedComments"
                className="optional-comment"
                attribute="DerivedComments"
                {...this.props.DerivedComments}
                onUpdate={this.updateDerivedComments}
                required={false}
              />
            )}
          </div>
        )}

        {this.props.CitizenshipStatus?.value === 'NotCitizen' && (
          <div>
            {this.props.requireCitizenshipStatusResidenceStatus && (
              <Field
                title={i18n.t('citizenship.status.heading.residencestatus')}
                scrollIntoView={this.props.scrollIntoView}
                attribute="ResidenceStatus"
              >
                <Text
                  name="ResidenceStatus"
                  className="residence-status"
                  {...this.props.ResidenceStatus}
                  onUpdate={(value) => {
                    this.updateField('ResidenceStatus', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="ResidenceStatus"
                />
              </Field>
            )}

            <Field
              title={i18n.t('citizenship.status.heading.entrydate')}
              help="citizenship.status.help.entrydate"
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
              attribute="EntryDate"
              exactAttribute
            >
              <NotApplicable
                name="EntryDateNotApplicable"
                {...this.props.EntryDateNotApplicable}
                label={i18n.t('citizenship.label.notApplicable')}
                or={i18n.t('citizenship.label.or')}
                onUpdate={this.updateEntryDateNotApplicable}
                attribute="EntryDateNotApplicable"
                includeNAConfirmation={Boolean(this.props.EntryDate
                  && (this.props.EntryDate.day
                    || this.props.EntryDate.month
                    || this.props.EntryDate.year
                  ))}
              >
                <DateControl
                  name="EntryDate"
                  className="entry-date"
                  {...this.props.EntryDate}
                  minDateEqualTo
                  onUpdate={(value) => {
                    this.updateField('EntryDate', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="EntryDate"
                />
              </NotApplicable>
              {this.props.EntryDateNotApplicable?.applicable === false && (
                <Field
                  title={i18n.t('citizenship.status.label.explanation')}
                  titleSize="label"
                  attribute="EntryDateExplanation"
                  adjustFor="textarea"
                >
                  <Textarea
                    attribute="EntryDateExplanation"
                    name="EntryDateExplanation"
                    className="entry-date-explanation"
                    {...this.props.EntryDateExplanation}
                    onUpdate={(value) => {
                      this.updateField('EntryDateExplanation', value);
                    }}
                    onError={this.handleError}
                  />
                </Field>
              )}
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.priorcitizenship.notcitizen')}
              help="citizenship.status.help.priorcitizenship"
              scrollIntoView={this.props.scrollIntoView}
              attribute="PriorCitizenship"
            >
              <Country
                name="PriorCitizenship"
                className="prior-citizenship"
                {...this.props.PriorCitizenship}
                multiple
                onUpdate={(value) => {
                  this.updateField('PriorCitizenship', value);
                }}
                onError={this.handleError}
                required={this.props.required}
                attribute="PriorCitizenship"
                excludeUnitedStates
              />
            </Field>

            <Field
              title={i18n.t('citizenship.status.heading.entrylocation')}
              adjustFor="address"
              scrollIntoView={this.props.scrollIntoView}
              attribute="EntryLocation"
            >
              <NotApplicable
                name="EntryLocationNotApplicable"
                {...this.props.EntryLocationNotApplicable}
                label={i18n.t('citizenship.label.notApplicable')}
                or={i18n.t('citizenship.label.or')}
                onUpdate={this.updateEntryLocationNotApplicable}
                attribute="EntryLocationNotApplicable"
                includeNAConfirmation={Boolean(this.props.EntryLocation
                  && (this.props.EntryLocation.city
                    || this.props.EntryLocation.state
                  ))}
              >
                <Location
                  name="EntryLocation"
                  className="entry-location"
                  {...this.props.EntryLocation}
                  layout={Location.CITY_STATE}
                  onUpdate={(value) => {
                    this.updateField('EntryLocation', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="EntryLocation"
                />
              </NotApplicable>
            </Field>
            {this.props.EntryLocationNotApplicable?.applicable === false && (
              <Field
                title={i18n.t('citizenship.status.label.explanation')}
                titleSize="label"
                attribute="EntryLocationExplanation"
                adjustFor="textarea"
              >
                <Textarea
                  attribute="EntryLocationExplanation"
                  name="EntryLocationExplanation"
                  className="entry-location-explanation"
                  {...this.props.EntryLocationExplanation}
                  onUpdate={(value) => {
                    this.updateField('EntryLocationExplanation', value);
                  }}
                  onError={this.handleError}
                />
              </Field>
            )}

            <Field
              title={i18n.t('citizenship.status.heading.alienregistrationnumber.notcitizen')}
              scrollIntoView={this.props.scrollIntoView}
              attribute="AlienRegistrationNumber"
            >
              <NotApplicable
                name="AlienRegistrationNumberNotApplicable"
                {...this.props.AlienRegistrationNumberNotApplicable}
                label={i18n.t('citizenship.label.notApplicable')}
                or={i18n.t('citizenship.label.or')}
                onUpdate={this.updateAlienRegistrationNumberNotApplicable}
                attribute="AlienRegistrationNumberNotApplicable"
                includeNAConfirmation={Boolean(this.props.AlienRegistrationNumber?.value)}
              >
                <Text
                  name="AlienRegistrationNumber"
                  className="alien-registration-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.AlienRegistrationNumber}
                  onUpdate={(value) => {
                    this.updateField('AlienRegistrationNumber', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="AlienRegistrationNumber"
                />
              </NotApplicable>
              {this.props.AlienRegistrationNumberNotApplicable?.applicable === false && (
                <Field
                  title={i18n.t('citizenship.status.label.explanation')}
                  titleSize="label"
                  attribute="AlienRegistrationNumberExplanation"
                  adjustFor="textarea"
                >
                  <Textarea
                    attribute="AlienRegistrationNumberExplanation"
                    name="AlienRegistrationNumberExplanation"
                    className="alien-registration-number-explanation"
                    {...this.props.AlienRegistrationNumberExplanation}
                    onUpdate={(value) => {
                      this.updateField('AlienRegistrationNumberExplanation', value);
                    }}
                    onError={this.handleError}
                  />
                </Field>
              )}
            </Field>
            {this.props.requireCitizenshipStatusNotCitizenDocumentFields && (
              <Field
                title={i18n.t('citizenship.status.heading.documentexpiration')}
                adjustFor="datecontrol"
                scrollIntoView={this.props.scrollIntoView}
                attribute="DocumentExpiration"
              >
                <DateControl
                  name="DocumentExpiration"
                  className="document-expiration"
                  {...this.props.DocumentExpiration}
                  noMaxDate
                  minDateEqualTo
                  onUpdate={(value) => {
                    this.updateField('DocumentExpiration', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="DocumentExpiration"
                />
              </Field>
            )}

            {this.props.requireCitizenshipStatusNotCitizenDocumentFields && (
              <Field
                title={i18n.t('citizenship.status.heading.documenttype')}
                adjustFor="buttons"
                scrollIntoView={this.props.scrollIntoView}
                attribute="DocumentType"
              >
                <RadioGroup
                  className="citizenship-document-type option-list option-list-vertical"
                  required={this.props.required}
                  onError={this.handleError}
                  selectedValue={this.props.DocumentType?.value}
                  attribute="DocumentType"
                >
                  <Radio
                    name="document-type-i94"
                    label={i18n.t('citizenship.status.label.documenttype.i94')}
                    value="I-94"
                    className="document-type-i94"
                    onUpdate={this.updateDocumentType}
                    onError={this.handleError}
                  />
                  <Radio
                    name="document-type-visa"
                    label={i18n.t('citizenship.status.label.documenttype.visa')}
                    value="U.S. Visa"
                    className="document-type-visa"
                    onUpdate={this.updateDocumentType}
                    onError={this.handleError}
                  />
                  <Radio
                    name="document-type-i20"
                    label={i18n.t('citizenship.status.label.documenttype.i20')}
                    value="I-20"
                    className="document-type-i20"
                    onUpdate={this.updateDocumentType}
                    onError={this.handleError}
                  />
                  <Radio
                    name="document-type-ds2019"
                    label={i18n.t('citizenship.status.label.documenttype.ds2019')}
                    value="DS-2019"
                    className="document-type-ds2019"
                    onUpdate={this.updateDocumentType}
                    onError={this.handleError}
                  />
                  <Radio
                    name="document-type-other"
                    label={i18n.t('citizenship.status.label.documenttype.other')}
                    value="Other"
                    className="document-type-other"
                    onUpdate={this.updateDocumentType}
                    onError={this.handleError}
                  />
                </RadioGroup>

                {this.props.DocumentType?.value === 'Other' && (
                  <Field
                    title={i18n.t('citizenship.status.label.explanation')}
                    titleSize="label"
                    adjustFor="textarea"
                    scrollIntoView={this.props.scrollIntoView}
                    attribute="Explanation"
                  >
                    <Textarea
                      name="Explanation"
                      className="citizenship-document-type-explanation"
                      {...this.props.Explanation}
                      onUpdate={(value) => {
                        this.updateField('Explanation', value);
                      }}
                      onError={this.handleError}
                      required={this.props.required}
                      attribute="Explanation"
                    />
                  </Field>
                )}
              </Field>
            )}

            {this.props.requireCitizenshipStatusNotCitizenDocumentFields && this.props.DocumentType?.value !== 'Other' && (
              <Field
                title={i18n.t('citizenship.status.heading.documentnumber.notcitizen')}
                scrollIntoView={this.props.scrollIntoView}
                attribute="DocumentNumber"
              >
                <Text
                  name="DocumentNumber"
                  className="document-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onUpdate={(value) => {
                    this.updateField('DocumentNumber', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="DocumentNumber"
                />
              </Field>
            )}

            {this.props.requireCitizenshipStatusNotCitizenDocumentFields && this.props.DocumentType?.value !== 'Other' && (
              <Field
                title={i18n.t('citizenship.status.heading.documentname')}
                filterErrors={Name.requiredErrorsOnly}
                optional
              >
                <Name
                  name="DocumentName"
                  aria-label="Provide the name in which the document was issued"
                  className="document-name"
                  {...this.props.DocumentName}
                  onUpdate={(value) => {
                    this.updateField('DocumentName', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute="DocumentName"
                />
              </Field>
            )}

            {this.props.requireCitizenshipStatusNotCitizenDocumentFields && this.props.DocumentType?.value !== 'Other' && (
              <Field
                title={i18n.t('citizenship.status.heading.documentissued')}
                adjustFor="datecontrol"
                scrollIntoView={this.props.scrollIntoView}
                attribute="DocumentIssued"
              >
                <DateControl
                  name="DocumentIssued"
                  className="document-issued"
                  {...this.props.DocumentIssued}
                  minDateEqualTo
                  onUpdate={(value) => {
                    this.updateField('DocumentIssued', value);
                  }}
                  onError={this.handleError}
                  required={this.props.required}
                  attribute="DocumentIssued"
                />
              </Field>
            )}
            {this.props.requireCitizenshipStatusAlienRegistrationExpiration && this.props.DocumentType?.value === 'U.S. Visa' && (
              // AlienRegistrationExpiration is the Visa Expiration
              <Field
                title={i18n.t('citizenship.status.heading.alienregistrationexpiration')}
                adjustFor="datecontrol"
                scrollIntoView={this.props.scrollIntoView}
                attribute="AlienRegistrationExpiration"
              >
                <DateControl
                  name="AlienRegistrationExpiration"
                  className="alien-registration-expiration"
                  {...this.props.AlienRegistrationExpiration}
                  noMaxDate
                  onUpdate={(value) => {
                    this.updateField('AlienRegistrationExpiration', value);
                  }}
                  onError={this.handleError}
                  required={
                    !!this.props.AlienRegistrationExpiration.month
                    || !!this.props.AlienRegistrationExpiration.day
                    || !!this.props.AlienRegistrationExpiration.year
                  }
                  attribute="AlienRegistrationExpiration"
                />
              </Field>
            )}
            {!isFormSF852013(formType, formVersion) && (
              <Comments
                title={i18n.t('citizenship.comments.optional')}
                name="AlienRegistrationComments"
                className="optional-comment"
                attribute="AlienRegistrationComments"
                {...this.props.AlienRegistrationComments}
                onUpdate={this.updateAlienRegistrationComments}
                required={false}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

Status.defaultProps = {
  CitizenshipStatus: {},
  AbroadDocumentation: {},
  Explanation: {},
  DocumentNumber: {},
  DocumentNumberNotApplicable: { applicable: true },
  DocumentIssued: {},
  PlaceIssued: {},
  DocumentName: {},
  CertificateNumber: {},
  CertificateIssued: {},
  CertificateName: {},
  CertificateMotherMaidenName: {},
  BornOnMilitaryInstallation: {},
  MilitaryBase: {},
  EntryDate: {},
  EntryDateNotApplicable: { applicable: true },
  EntryLocation: {},
  EntryLocationNotApplicable: { applicable: true },
  PriorCitizenship: {},
  HasAlienRegistration: {},
  AlienRegistrationNumber: {},
  AlienRegistrationNumberNotApplicable: { applicable: true },
  AlienRegistrationExpiration: {},
  CertificateCourtName: {},
  CertificateCourtAddress: {},
  Basis: {},
  PermanentResidentCardNumber: {},
  CitizenshipCertificateCardNumberNotApplicable: { applicable: true },
  ResidenceStatus: {},
  ResidenceStatusExplanation: {},
  DocumentType: {},
  DocumentTitle: {},
  DocumentExpiration: {},
  DocumentExpirationNotApplicable: { applicable: true },
  Form240DateExplanation: {},
  hasValidUSPassport: false,
  CitizenshipStatusComments: {},
  AlienRegistrationComments: {},
  AlienRegistrationNumberComments: {},
  DerivedComments: {},
  MilitaryBaseComments: {},
  ProofOfUSCitizenshipComments: {},
  CertificateComments: {},
  NaturalizationComments: {},
  EntryDateExplanation: {},
  EntryLocationExplanation: {},
  AlienRegistrationNumberExplanation: {},
  IsResidentOfUnitedStates: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  scrollIntoView: false,
  errors: [],
};

export default connectSubsection(Status, sectionConfig);
